'use client';

import { useTranslations } from 'next-intl';
import type { KagemaneQuote } from '@/lib/api/video-replications';
import type { KagemaneQuoteStatus } from '@/lib/use-kagemane-quote';

type CostSnapshot = {
  authenticated: boolean; hasSource: boolean; quote: KagemaneQuote | null; status: KagemaneQuoteStatus;
  useGift: boolean; giftReady: boolean;
  giftStatus?: 'loading' | 'error' | 'ready' | 'unavailable';
};

/** The current cost replaces the action label in both responsive buttons. */
export function useKagemaneGenerationLabel({ authenticated, hasSource, quote, status, useGift, giftReady, giftStatus = 'loading' }: CostSnapshot) {
  const t = useTranslations('kagemane');
  const error = useGift ? giftStatus === 'error' || giftStatus === 'unavailable' || status === 'error' : status === 'error';
  return !authenticated ? t('costSignIn') : !hasSource ? t('costChooseVideo')
    : useGift ? error ? t('costUnavailable') : giftReady && status === 'ready' && quote?.gift_applied === true
      ? t('costGift', {credits: quote.credits ?? 0}) : t('costGiftChecking')
      : error ? t('costUnavailable') : quote ? quote.pricing_mode === 'unplanned' ? t('costUsage') : quote.pricing_mode === 'range'
        ? t('costRange', { min: quote.estimate_min, max: quote.estimate_max }) : t('costFixed', { credits: quote.credits })
        : t('costLoading');
}

/** Only an actionable error hint sits outside the button; ready costs are inside. */
export function KagemaneGenerationCost({ authenticated, hasSource, quote, status, useGift, giftStatus = 'loading', disabled, onRetry }: CostSnapshot & {
  disabled: boolean; onRetry: () => void;
}) {
  const t = useTranslations('kagemane');
  const error = useGift ? giftStatus === 'error' || giftStatus === 'unavailable' || status === 'error' : status === 'error';
  if (!authenticated || !hasSource) return null;
  if (!error && !useGift && quote?.pricing_mode === 'unplanned') {
    return <p className="mb-2 text-xs leading-5 text-muted-foreground" role="status" aria-live="polite" data-kagemane-generation-cost>{t('costUsageHint')}</p>;
  }
  if (!error) return null;
  const message = useGift ? t(giftStatus === 'unavailable' ? 'costGiftUnavailable' : 'costGiftError') : t('costError');
  return <div className="mb-2 flex min-h-6 items-center justify-between gap-2 text-xs leading-5" data-kagemane-generation-cost>
    <p role="alert" aria-live="polite" aria-atomic="true" className="text-red-600">{message}</p>
    <button type="button" disabled={disabled} onClick={onRetry}
      className="shrink-0 rounded-md px-1.5 py-1 text-[#176B89] underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] disabled:opacity-50">{t('costRetry')}</button>
  </div>;
}
